/**
 * clickhouse-setup.ts — the customer grant script for a ClickHouse account (grants reference 2.1).
 *
 * The customer provisions the user and its password; this script only grants. The grants and the
 * identifier rules live in src/lib/clickhouse-grants.ts, which the e2e fixture users share.
 */

import { clickHouseSetupScript } from '@server/clickhouse-grants';
import type { ClickHouseCoordinates } from '@server/types';

/** The administrator needs to know what the script creates and which databases the user can access. */
export const CLICKHOUSE_SETUP_DESCRIPTION =
  'Ask a ClickHouse administrator to run this before you validate. Create the user and its password first. The script creates _rudderstack to store sync state. The user can then read your database and write to the working database.';

/**
 * The order matters for ClickHouse: the RudderStack account is already gone, so nothing syncs while the
 * admin works. The teardown SQL from config-backend drops only the user. It never drops the working
 * database, because other RudderStack accounts on the same server can share it.
 */
export const CLICKHOUSE_TEARDOWN_STEPS = [
  'Copy the SQL below.',
  'Ask a ClickHouse administrator to run it. It removes the RudderStack user. It keeps the RudderStack working database (default _rudderstack), because other RudderStack accounts on this server can use it. Drop that database yourself only after no RudderStack account uses it.',
  'Delete this connection in Lookout if you do not plan to reconnect it.',
];

export type ClickHouseSetupSQLParams = Pick<ClickHouseCoordinates, 'database' | 'user' | 'rudderSchema'>;

export function generateClickHouseSetupSQL(params: ClickHouseSetupSQLParams): string {
  return clickHouseSetupScript(params);
}
